import { Grid, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "components/datePicker";
import { LocalizeText } from "components/localizer";
import CommunityContext from "contexts/community";
import { format, isValid as isValidDate, parseISO } from "date-fns";
import React from "react";
import { dateFormats } from "utils/formats";
import { IUsePaginationStore } from "utils/usePaginationStore";

interface IMembershipFilterInputProps {
    membershipPagination: IUsePaginationStore<"memberships">;
}

interface IState {
    start: Date | null;
    end: Date | null;
    subscriptionsFrom: number;
    subscriptionsTo: number;
    communities: string[];
}

const filterParamMapping: Record<keyof IState, string> = {
    start: "end_date__gte",
    end: "end_date__lte",
    subscriptionsFrom: "min_subscription_count",
    subscriptionsTo: "max_subscription_count",
    communities: "communities",
};

const MembershipFilterInput: React.FC<IMembershipFilterInputProps> = ({ membershipPagination }) => {
    const { communitiesPagination } = React.useContext(CommunityContext);

    const [state, setState] = React.useState<IState>({
        start: null,
        end: null,
        subscriptionsFrom: 0,
        subscriptionsTo: 0,
        communities: [],
    });

    React.useEffect(() => {
        const start = membershipPagination.getParam("end_date__gte");
        const end = membershipPagination.getParam("end_date__lte");
        const subscriptionsFrom = membershipPagination.getParam("min_subscription_count");
        const subscriptionsTo = membershipPagination.getParam("max_subscription_count");
        const communities = membershipPagination.getParam("communities");

        setState({
            start: start == null ? null : parseISO(start as string),
            end: end == null ? null : parseISO(end as string),
            subscriptionsFrom: subscriptionsFrom == null ? 0 : subscriptionsFrom as number,
            subscriptionsTo: subscriptionsTo == null ? 0 : subscriptionsTo as number,
            communities: communities == null || communities === "" ? [] : (communities as string).split(","),
        });
        communitiesPagination.getInitial();
        // eslint-disable-next-line
    }, []);

    const onChange = (type: keyof IState, value: string | number | Date): void => {
        if (value) {
            setState({ ...state, [type]: value });
            if (value instanceof Date) {
                if (!isValidDate(value)) {
                    return;
                }
                value = format(value, dateFormats.APIDATE);
            }
            membershipPagination.setParam(filterParamMapping[type], value.toString());
            membershipPagination.getInitial();
        } else {
            membershipPagination.setParam(filterParamMapping[type], null);
        }
    };

    return (
        <Grid container justifyContent={"flex-end"} alignItems={"baseline"} rowSpacing={4}>
            <Grid item xs={4}>
                <LocalizeText tag="contractDuration" />
            </Grid>
            <Grid container item xs={8} alignItems={"baseline"} flexWrap={"nowrap"}>
                <DatePicker
                    format={dateFormats.WEBDATE}
                    label={<LocalizeText tag={"from"} />}
                    value={state.start}
                    slotProps={{
                        textField: {
                            sx: { paddingRight: "1em" },
                            fullWidth: true,
                            variant: "standard",
                            size: "small",
                        },
                    }}
                    onChange={(value: Date) => onChange("start", value as Date)}
                />
                <DatePicker
                    format={dateFormats.WEBDATE}
                    label={<LocalizeText tag={"to"} />}
                    value={state.end}
                    slotProps={{ textField: { variant: "standard", fullWidth: true, size: "small" } }}
                    onChange={(value: Date) => onChange("end", value as Date)}
                />
            </Grid>
            <Grid item xs={4}>
                <LocalizeText tag="numberOfSubscriptions" />
            </Grid>
            <Grid container item xs={8} flexWrap={"nowrap"}>
                <TextField
                    sx={{ paddingRight: "1em", width: "100%" }}
                    label={<LocalizeText tag={"fromAlt"} />}
                    variant="standard"
                    size="small"
                    value={state.subscriptionsFrom}
                    onChange={(event) => onChange("subscriptionsFrom", event.target.value)}
                />
                <TextField
                    sx={{ width: "100%" }}
                    label={<LocalizeText tag={"to"} />}
                    variant="standard"
                    size="small"
                    value={state.subscriptionsTo}
                    onChange={(event) => onChange("subscriptionsTo", event.target.value)}
                />
            </Grid>
            <Grid item xs={4}>
                <LocalizeText tag="arenas" />
            </Grid>
            <Grid container item xs={8} alignItems={"baseline"} flexWrap={"nowrap"}>
                <TextField
                    select
                    variant="standard"
                    fullWidth
                    SelectProps={{
                        multiple: true,
                    }}
                    value={state.communities}
                    onChange={(event) => onChange("communities", event.target.value)}
                >
                    {communitiesPagination.results.map((item) => (
                        <MenuItem key={item.uuid} value={item.uuid}>
                            {item.title}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>
    );
};

export default MembershipFilterInput;
